import { useCallback } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { ButtonProps } from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";

import { ManagementStrategy } from "../Settings/types";
import { showDeleteConfirmation } from "./DeleteConfirmation";

type LoginPolicyManageDropdownProps = {
  handleLoginPolicyEditDetailsClick: () => void;
  loginPolicyDeleteCallback?: () => void;
  refetchQueriesOnDelete?: string[];
  id: string;
  name: string;
  buttonVariant?: ButtonProps["variant"];
  size?: "small" | "medium";
  isLoginPolicyActive: boolean;
  analyticsPage:
    | AnalyticsPageOrganization.OrganizationLoginPoliciesList
    | AnalyticsPageOrganization.OrganizationLoginPolicyDetails;
};

const LoginPolicyManageDropdown = ({
  handleLoginPolicyEditDetailsClick,
  loginPolicyDeleteCallback,
  refetchQueriesOnDelete,
  id,
  name,
  buttonVariant = "secondary",
  size = "small",
  isLoginPolicyActive,
  analyticsPage,
}: LoginPolicyManageDropdownProps) => {
  const managementStrategy = isLoginPolicyActive
    ? ManagementStrategy.LOGIN_POLICY
    : ManagementStrategy.USER_MANAGEMENT;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
    defaultCallbackTrackProperties: { managementStrategy },
  });

  const onDeleteClick = () => {
    showDeleteConfirmation({
      isLoginPolicyActive,
      name,
      id,
      refetchQueries: refetchQueriesOnDelete,
    }).then((confirmed) => {
      if (confirmed) {
        trackSegmentAnalyticsEvent("Confirm Delete");
        loginPolicyDeleteCallback?.();
      }
    });
  };

  const editCallback = useCallback(() => {
    trackSegmentAnalyticsEvent("Edit details click");
    handleLoginPolicyEditDetailsClick();
  }, [handleLoginPolicyEditDetailsClick, trackSegmentAnalyticsEvent]);

  return (
    <DropdownMenuEllipsis
      dotsSize={size}
      buttonVariant={buttonVariant}
      tooltip="Login policy actions"
    >
      <DropdownMenuItem onAction={editCallback}>Edit details</DropdownMenuItem>
      <CopyFieldDropdownMenuItem title="Copy ID" value={id} />
      <DropdownMenuItem onAction={onDeleteClick} danger>
        Delete
      </DropdownMenuItem>
    </DropdownMenuEllipsis>
  );
};

export default LoginPolicyManageDropdown;
